import type {
  OrgProjectContextOption,
  OrgProjectFallbackContext,
} from "@arcadeai/ui-kit/org-project-context/types";
import { getRouteApi } from "@tanstack/react-router";
import type { FileRoutesByTo } from "@/routeTree.gen";

export type OrgProjectContext = OrgProjectFallbackContext;

export type ValidatedOrgProjectContext = OrgProjectContext & {
  organizations: OrgProjectContextOption[];
  projects: OrgProjectContextOption[];
};

export type UseOrgProjectContextResult = {
  organizations: OrgProjectContextOption[];
  projects: OrgProjectContextOption[];
  activeOrganization: OrgProjectContextOption | null;
  activeProject: OrgProjectContextOption | null;
  activeOrganizationId: string | null;
  activeProjectId: string | null;
  isLoadingOrganizations: boolean;
  isLoadingProjects: boolean;
  isReady: boolean;
  isError: boolean;
};

const ORG_PROJECT_ROUTE_ID =
  "/_authenticated/orgs/$organizationId/projects/$projectId";

export const ORG_PROJECT_ROUTE_PATH =
  "/orgs/$organizationId/projects/$projectId" as const;

/** A route inside the active project; links to it take `{ organizationId, projectId }` as params. */
export type ProjectRoutePath = Extract<
  keyof FileRoutesByTo,
  `${typeof ORG_PROJECT_ROUTE_PATH}/${string}`
>;

export const orgProjectRouteApi = getRouteApi(ORG_PROJECT_ROUTE_ID);

const DASHBOARD_HOME_SUFFIX = "/home";

// Emails and bookmarks still link to the unscoped `/dashboard` home page.
const HOME_SUFFIX_ALIASES = new Set(["/", "/dashboard", "/dashboard/"]);

export function buildOrgProjectPath(
  pathnameSuffix: string,
  scope: OrgProjectContext
): `/${string}` {
  const normalizedSuffix = HOME_SUFFIX_ALIASES.has(pathnameSuffix)
    ? DASHBOARD_HOME_SUFFIX
    : pathnameSuffix;
  const suffix = normalizedSuffix.startsWith("/")
    ? normalizedSuffix
    : `/${normalizedSuffix}`;

  return `/orgs/${scope.organizationId}/projects/${scope.projectId}${suffix}`;
}

// biome-ignore lint/performance/noBarrelFile: backward-compatible re-export from shared library
export { OrgProjectRouteError } from "@arcadeai/ui-kit/org-project-context/types";
